/**
 * The one action that wipes every practice-scoped slice.
 *
 * Lives in its own dependency-free module so callers (the practice switcher)
 * can dispatch it without importing rootReducer — which imports every slice,
 * each of which imports the store. Keeping the constant here keeps that cycle
 * out of the component layer.
 *
 * Handled in src/redux/rootReducer.js; see PRACTICE_AGNOSTIC_SLICES there for
 * what survives.
 */
import { clearAllStaticLookupCache } from '../utils/staticLookupCache';
import { clearIdentityCache } from '../utils/leadIdentityCache';

export const PRACTICE_SWITCH_RESET = 'app/practiceSwitchReset';

export const practiceSwitchReset = () => {
    // Practice-scoped entries in the sessionStorage TTL cache (e.g.
    // get_context:practice:<id> — see src/utils/staticLookupCache.js) must not
    // leak into the incoming practice's first render. Cheap side effect, run
    // unconditionally alongside every dispatch of this action rather than
    // relying on each call site to remember it.
    clearAllStaticLookupCache();
    // Identity-check results belong to the outgoing practice.
    clearIdentityCache();
    return { type: PRACTICE_SWITCH_RESET };
};

// ---------------------------------------------------------------------------
// Practice-switch store reset
// ---------------------------------------------------------------------------
// Almost every slice below holds data owned by ONE practice — leads, threads,
// reports, table tabs, payments. Historically the practice switcher cleared a
// hand-picked ~20 of them and relied on `navigate('/dashboard')` to unmount
// whatever was on screen. Now that a switch keeps the operator on the same
// page, "cleared a hand-picked few" is not good enough: any slice left holding
// the previous practice's rows renders them under the new practice until that
// page's own fetch resolves.
//
// So the default is inverted. On PRACTICE_SWITCH_RESET every slice returns to
// its initial state EXCEPT the ones listed here, which are either not
// practice-scoped or own a lifecycle that a blind reset would break. A slice
// added tomorrow inherits the safe behaviour without anyone remembering to
// wire it up.
export const PRACTICE_AGNOSTIC_SLICES = [
    // Identity of the switch itself — holds the practice list and the NEW id.
    'practiceTreatmentList',
    // Overwritten atomically by fetchMyPermissions(newPracticeId). Clearing it
    // eagerly flips hasTasksV2 to false during the network window and flashes
    // the wrong Tasks nav target (see the note in DashboardHeader).
    'permissions',
    // User-level, not practice-level.
    'user',
    'afterLogin',
    'forgotpassword',
    'loaderReducer',
    // Group memberships span practices and are only fetched by the group pages;
    // resetting would silently hide the group nav entry until a page refetched.
    'groupDashboard',
    // Also carries the settings-menu UI state the sidebar reads, and the
    // dashboard layout refetches its values on every practice change.
    'mergeTags',
    // Read by the always-mounted contacts popover, which fetches on open.
    'posts',
    // Owns live PieSocket subscription handles. A blind reset drops the handles
    // without unsubscribing, leaking the previous practice's message toasts —
    // exactly what startInitializeWhatsapp() goes out of its way to prevent.
    // Its own practice-keyed init already wipes threads/messages on switch.
    'whatsappV2',
    // Global super-admin surfaces. A practice switch is a context change for
    // the rest of the app; it should not wipe an admin's loaded management
    // tables, which are not practice-scoped in the first place.
    'superAdmin',
    'superAdminUsers',
    'superAdminDomains',
    'superAdminPractices',
    'scopedAdminUsers',
    'systemTemplates',
    'consentV2',
    'tasksV2Migration',
    'tasksV2DryRunPreview',
    'tasksV2ExecuteResults',
    'tasksV2DefaultTemplates',
    'tasksV2ChecklistConfig',
    'tasksV2CoverageMatrix',
    'tasksV2MirrorActivity',
];

/**
 * Project a root state down to only the slices that survive a practice switch.
 *
 * combineReducers re-initialises every key absent from the object it is given,
 * so handing it this projection is what performs the reset.
 *
 * @param {object|undefined} state          current root state
 * @param {string[]}         preservedKeys  slice names to carry over
 * @returns {object} a new object holding only the preserved slices
 */
export function projectPreservedState(state, preservedKeys = PRACTICE_AGNOSTIC_SLICES) {
    const preserved = {};
    preservedKeys.forEach((key) => {
        if (state?.[key] !== undefined) preserved[key] = state[key];
    });
    return preserved;
}
